import React from 'react';
import { TouchableOpacity, View } from 'react-native';

import { formatShiftRange } from '../../../common/utils/people/roster';
import { getLocalTime } from '../../../common/utils/time/time';
import { formatDate } from '../../../common/utils/utils';

import {
	Button,
	Heading,
	Label,
	Text,
} from '../../components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import MachineFillAvatar from './board/MachineFillAvatar';

// Header card of the classic My day board: day heading, clock and shift chips
// and the links to the brief, pick list summary and employee tools.
const DriverMyDayHeader = ({
	user,
	tenant,
	trainingContext,
	isTraining,
	serviceRun,
	clockStatus,
	isClockedOutForDay,
	rosterShift,
	machineFill,
	todayDateKey,
	hasEligiblePickListItems,
	openBrief,
	openPickListSummary,
	onViewEmployeeDay,
	onShowPickingWork,
}) => {
	const styles = useThemedStyles(createStyles);

	// After an end-of-day clock-out the yellow "Not clocked in" warning would
	// misread as a problem — the driver has finished, not forgotten to clock in
	// (isClockedOutForDay). A mid-day clock-out (stops remaining) keeps the
	// yellow warning because the driver still has to clock back in to continue.
	const clockChip = (
		<Label
			label={clockStatus.isClockedIn ? 'Clocked in' : isClockedOutForDay ? 'Clocked out' : 'Not clocked in'}
			green={clockStatus.isClockedIn}
			secondary={isClockedOutForDay}
			yellow={!clockStatus.isClockedIn && !isClockedOutForDay}
			style={styles.headerChip}
		/>
	);
	const shiftLabel = rosterShift
		? `Shift ${formatShiftRange(rosterShift.startMinutes, rosterShift.endMinutes)}`
		: null;
	const todayDisplayDate = formatDate(getLocalTime(tenant?.timezone), 'DAY_MONTHWORD_YEAR');

	// The fill avatar is day-level and only meaningful with assigned stops; in
	// training the day ratio is not the trainee's own run, so hide the glyph
	// entirely rather than show a misleading fill (mirrors the "All set" card).
	const showMachineFill = !isTraining && machineFill.totalStops > 0;
	const machineFillStorageKey = showMachineFill
		? `myday_machine_fill_done_${user?.id || 'user'}_${todayDateKey}`
		: null;

	return (
		<View style={styles.headerCard}>
			<View style={styles.headerTopRow}>
				{
					showMachineFill && (
						<MachineFillAvatar
							ratio={machineFill.ratio}
							isComplete={machineFill.isComplete}
							flourishStorageKey={machineFillStorageKey}
							accessibilityLabel={`${machineFill.doneStops} of ${machineFill.totalStops} machines done`}
						/>
					)
				}
				<View style={styles.headerHeadingWrap}>
					<Heading
						heading={isTraining ? `${trainingContext.targetDriverName}'s My day` : 'My day'}
						subHeading={serviceRun ? `${serviceRun.route?.name || 'Routeless service run'} · ${todayDisplayDate}` : todayDisplayDate}
					/>
				</View>
				<View style={styles.headerLinks}>
					{
						Boolean(serviceRun?.brief) && (
							<TouchableOpacity onPress={openBrief}>
								<Text style={styles.headerLinkText}>Run brief</Text>
							</TouchableOpacity>
						)
					}
					{
						hasEligiblePickListItems && (
							<TouchableOpacity onPress={openPickListSummary}>
								<Text style={styles.headerLinkText}>Pick list summary</Text>
							</TouchableOpacity>
						)
					}
				</View>
			</View>
			<View style={styles.headerChipsRow}>
				{clockChip}
				{
					Boolean(shiftLabel) && (
						<Label label={shiftLabel} secondary={true} style={styles.headerChip} />
					)
				}
			</View>
			{
				Boolean(onViewEmployeeDay) && (
					<Button
						label="View an employee's day"
						onPress={onViewEmployeeDay}
						style={styles.employeePreviewButton}
					/>
				)
			}
			{
				Boolean(onShowPickingWork) && (
					<Button
						label="Help with picking"
						onPress={onShowPickingWork}
						secondary={true}
						style={styles.employeePreviewButton}
					/>
				)
			}
		</View>
	);
};

const createStyles = (theme) => ({
	headerCard: {
		backgroundColor: theme.mainBg1,
		borderRadius: 12,
		padding: 12,
		marginBottom: 10,
		borderWidth: 1,
		borderColor: theme.border3,
	},
	headerTopRow: {
		flexDirection: 'row',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
	},
	// Long route names must wrap instead of pushing the row past the screen edge
	// (RN rows do not shrink children by default).
	headerHeadingWrap: {
		flex: 1,
		minWidth: 0,
		paddingRight: 8,
	},
	headerLinks: {
		alignItems: 'flex-end',
		flexShrink: 0,
	},
	headerLinkText: {
		fontSize: 12,
		color: theme.text2,
		textDecorationLine: 'underline',
		marginTop: 4,
	},
	headerChipsRow: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		marginTop: 8,
	},
	headerChip: {
		marginRight: 6,
		marginBottom: 0,
	},
	employeePreviewButton: {
		marginTop: 8,
	},
});

export default DriverMyDayHeader;
